/**
 * Product-domain column bundles for platform reports.
 *
 * Mirrors backend/shared/platform_reports/columns/product.py. Keep keys and
 * labels in sync - the column-sync test enforces matching keys per report.
 *
 * Reports compose bundles via spread:
 *
 *   const columns: ColumnDef[] = [
 *     ...FP_IDENTITY, ...PRODUCT_IDENTITY, ...PRICING_STOCK, ...XERO_ACCOUNTS,
 *   ]
 */
import type { ColumnDef } from '../BaseReportPage'

export const FP_IDENTITY: ColumnDef[] = [
  { key: 'fp_code', label: 'FP Code', type: 'text' },
  { key: 'franchise_name', label: 'Franchise Name', type: 'text', align: 'left', minWidth: 200 },
  { key: 'fp_status', label: 'FP Status', type: 'text' },
  { key: 'last_sync_at', label: 'Data Age', type: 'sync_status' },
]

export const PRODUCT_IDENTITY: ColumnDef[] = [
  {
    key: 'product_name',
    label: 'Product Name',
    type: 'text',
    align: 'left',
    minWidth: 220,
    link: { field: 'lightspeed_product_link', system: 'lightspeed', showIcon: true },
  },
  { key: 'sku', label: 'SKU', type: 'text' },
  { key: 'product_type_label', label: 'Type', type: 'text', align: 'center', filterable: true },
  { key: 'main_category', label: 'Main Category', type: 'text', align: 'left', filterable: true },
  { key: 'active', label: 'Status', type: 'text', filterable: true },
  { key: 'has_inventory', label: 'Inventory Tracking', type: 'text', filterable: true },
  { key: 'supplier_code', label: 'Supplier Code', type: 'text' },
]

export const PRICING_STOCK: ColumnDef[] = [
  { key: 'price_excluding_tax', label: 'Price (Ex)', type: 'currency', align: 'right' },
  { key: 'supply_price', label: 'Supply Price', type: 'currency', align: 'right' },
  { key: 'total_stock', label: 'Total Stock', type: 'integer', align: 'right' },
  { key: 'last_sold', label: 'Last Sold', type: 'date' },
  { key: 'aging', label: 'Aging', type: 'text', align: 'center', filterable: true },
]

// Xero account code error rule: blank cells are NOT an error - Lightspeed
// has a default sales/purchase account when unset, so an empty cell just
// means "use LS fallback". The invalid-flag is server-derived per row in
// the report query (regex + xo_coa existence; mirrors the tagger), and
// the cell renderer simply trusts the boolean.
export const XERO_ACCOUNTS: ColumnDef[] = [
  { key: 'account_code', label: 'Sales Code', type: 'text', error: { rowFlag: 'account_code_invalid' } },
  { key: 'sales_account_name', label: 'Sales Account Name', type: 'text', align: 'left' },
  { key: 'account_code_purchase', label: 'Purchase Code', type: 'text', error: { rowFlag: 'account_code_purchase_invalid' } },
  { key: 'purchase_account_name', label: 'Purchase Account Name', type: 'text', align: 'left' },
]

export function selectColumns(bundle: ColumnDef[], keys: string[]): ColumnDef[] {
  const set = new Set(keys)
  const picked = bundle.filter(c => set.has(c.key))
  const missing = keys.filter(k => !bundle.some(c => c.key === k))
  if (missing.length) throw new Error(`Keys not found in bundle: ${missing.join(', ')}`)
  return picked
}
